import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { FaChevronRight } from "react-icons/fa";

import { type AiInsightsResponse, getAiInsightsAvailabilityMessage, useAiInsightJob } from "../../../api/aiInsights";
import {
  type CriticalGapWorkstreamContextDto,
  useCriticalGapDay1ReadinessContext,
} from "../../../api/criticalGapAnalysis";
import { isApiError } from "../../../api/errors";
import { useIsDataQualityVisible } from "../../../api/userAccess";
import { AiOutputFeedbackControls } from "../../aiOutputFeedback/AiOutputFeedbackControls";
import { Spinner } from "../../shared";
import { AiDataQualitySection } from "../AiDataQualitySection";
import { AiInsightDeveloperContext, AiInsightSourceMarker } from "../AiInsightsRaidDetailParts";
import { AiInsightWaitingState } from "../AiInsightWaitingState";
import type {
  Day1ReadinessInsightDto,
  Day1ReadinessKeyRecommendationDto,
  Day1ReadinessWorkstreamCountsDto,
  Day1ReadinessWorkstreamDto,
} from "./AiInsightsDay1ReadinessTypes";
import { AiInsightsDay1ReadinessWorkstreamDetailModal } from "./AiInsightsDay1ReadinessWorkstreamDetailModal";

const PERSONA_NOT_ASSIGNED_ERROR_CODE = "no_persona";

export type AiInsightsDay1ReadinessShelfProps = {
  isOpen: boolean;
  insightResponse?: AiInsightsResponse;
};

function formatDisplayValue(value?: string | null) {
  if (value == null || value === "") {
    return null;
  }

  return value
    .split(/[_-]/g)
    .map((part) => (part.length > 0 ? `${part[0]?.toUpperCase() ?? ""}${part.slice(1)}` : part))
    .join(" ");
}

function formatCounts(counts?: Day1ReadinessWorkstreamCountsDto | null) {
  if (counts == null) {
    return null;
  }

  const items = [
    counts.red != null ? `Red ${counts.red}` : null,
    counts.amber != null ? `Amber ${counts.amber}` : null,
    counts.green != null ? `Green ${counts.green}` : null,
    counts.not_assessed != null ? `Not assessed ${counts.not_assessed}` : null,
  ].filter(Boolean);

  return items.length > 0 ? items.join(" · ") : null;
}

function Day1RecommendationCard({
  item,
  workstreams,
  contextWorkstreams,
  onSelectWorkstream,
}: {
  item: Day1ReadinessKeyRecommendationDto;
  workstreams: Day1ReadinessWorkstreamDto[];
  contextWorkstreams: CriticalGapWorkstreamContextDto[];
  onSelectWorkstream: (workstream: Day1ReadinessWorkstreamDto) => void;
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const affectedWorkstreams = Array.isArray(item.affected_workstreams) ? item.affected_workstreams : [];
  const meta = [
    item.priority != null ? `Priority ${item.priority}` : null,
    formatDisplayValue(item.recommendation_type),
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <article className="global-insights-overlay__raid-unrecorded-card global-insights-overlay__raid-unrecorded-card--ai-generated">
      <AiInsightSourceMarker />
      <button
        type="button"
        className="global-insights-overlay__raid-unrecorded-header"
        aria-expanded={isExpanded}
        onClick={() => setIsExpanded((current) => !current)}
      >
        <div className="global-insights-overlay__raid-unrecorded-summary">
          <p className="global-insights-overlay__raid-unrecorded-meta">{meta || "Recommended action"}</p>
          <h4 className="global-insights-overlay__raid-unrecorded-title">{item.title}</h4>
        </div>
        <span
          className={[
            "global-insights-overlay__raid-unrecorded-chevron",
            isExpanded ? "global-insights-overlay__raid-unrecorded-chevron--expanded" : "",
          ]
            .filter(Boolean)
            .join(" ")}
          aria-hidden="true"
        >
          <span className="Appkit4-icon icon-down-chevron-outline" />
        </span>
      </button>
      {isExpanded && (
        <div className="global-insights-overlay__raid-unrecorded-content">
          <p className="global-insights-overlay__raid-unrecorded-text">{item.description}</p>
          <hr className="global-insights-overlay__raid-unrecorded-divider" />
          <p className="global-insights-overlay__raid-unrecorded-label">Recommended action</p>
          <p className="global-insights-overlay__raid-unrecorded-text">{item.recommendation ?? "Not available"}</p>
          <hr className="global-insights-overlay__raid-unrecorded-divider" />
          <p className="global-insights-overlay__raid-unrecorded-label">Why this matters</p>
          <p className="global-insights-overlay__raid-unrecorded-text">{item.why_this_matters ?? "Not available"}</p>
          <hr className="global-insights-overlay__raid-unrecorded-divider" />
          <p className="global-insights-overlay__raid-unrecorded-label">Timeframe</p>
          <p className="global-insights-overlay__raid-unrecorded-text">{item.timeframe ?? "Not available"}</p>
          {affectedWorkstreams.length > 0 && (
            <div className="global-insights-overlay__raid-unrecorded-tags">
              <span className="global-insights-overlay__raid-unrecorded-tags-label">
                Affected workstreams (select to view details):
              </span>
              {affectedWorkstreams.map((workstream) => {
                const reference = workstream.trim();
                const normalizedReference = reference.toLowerCase();
                const matchedWorkstream = workstreams.find(
                  (candidate) =>
                    candidate.workstream_id?.trim().toLowerCase() === normalizedReference ||
                    candidate.workstream_name?.trim().toLowerCase() === normalizedReference
                );
                const matchedContextWorkstream =
                  matchedWorkstream == null
                    ? contextWorkstreams.find(
                        (candidate) =>
                          (candidate.workstreamId != null &&
                            String(candidate.workstreamId).trim().toLowerCase() === normalizedReference) ||
                          candidate.workstreamCode?.trim().toLowerCase() === normalizedReference ||
                          candidate.normalizedWorkstreamKey?.trim().toLowerCase() === normalizedReference ||
                          candidate.workstreamName?.trim().toLowerCase() === normalizedReference
                      )
                    : undefined;
                const displayName =
                  matchedWorkstream?.workstream_name ?? matchedContextWorkstream?.workstreamName;
                const displayId =
                  matchedWorkstream?.workstream_id?.trim() ??
                  (matchedContextWorkstream?.workstreamId != null
                    ? String(matchedContextWorkstream.workstreamId)
                    : matchedContextWorkstream?.workstreamCode?.trim());
                const selectedWorkstream =
                  matchedWorkstream ??
                  (matchedContextWorkstream != null
                    ? {
                        workstream_id: displayId ?? reference,
                        workstream_name: displayName ?? reference,
                      }
                    : undefined);

                return (
                  <button
                    key={reference}
                    type="button"
                    className="global-insights-overlay__raid-unrecorded-workstream-link"
                    aria-label={
                      selectedWorkstream == null
                        ? `Workstream ID ${reference}; details unavailable`
                        : `Open workstream ${displayName ?? reference}${displayId ? ` (ID ${displayId})` : ""}`
                    }
                    disabled={selectedWorkstream == null}
                    onClick={() => {
                      if (selectedWorkstream != null) {
                        onSelectWorkstream(selectedWorkstream);
                      }
                    }}
                  >
                    <span>{displayName ?? `Workstream ID ${reference}`}</span>
                    {displayId != null && displayId !== "" && (
                      <span className="global-insights-overlay__raid-unrecorded-workstream-id">
                        ID {displayId}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </article>
  );
}

export function AiInsightsDay1ReadinessShelf({ isOpen, insightResponse }: AiInsightsDay1ReadinessShelfProps) {
  const isDataQualityVisible = useIsDataQualityVisible();
  const day1ReadinessQuery = useAiInsightJob("day1_readiness", isOpen && insightResponse == null);
  const [selectedWorkstream, setSelectedWorkstream] = useState<Day1ReadinessWorkstreamDto | null>(null);

  useEffect(() => {
    if (!isOpen) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- reset stale selection when the overlay closes
      setSelectedWorkstream(null);
    }
  }, [isOpen]);

  const day1ReadinessResponse = insightResponse ?? day1ReadinessQuery.data?.data;
  const day1ReadinessInsight = day1ReadinessResponse?.insight as Day1ReadinessInsightDto | undefined;
  const day1ContextQuery = useCriticalGapDay1ReadinessContext(isOpen && day1ReadinessInsight != null);
  const selectedWorkstreamSource = (day1ContextQuery.data?.pageData?.workstreams ?? []).find(
    (workstreamSource) =>
      (selectedWorkstream?.workstream_id != null &&
        [
          workstreamSource.workstreamId == null ? "" : String(workstreamSource.workstreamId),
          workstreamSource.workstreamCode ?? "",
          workstreamSource.normalizedWorkstreamKey ?? "",
        ].some(
          (id) => id.trim().toLowerCase() === selectedWorkstream.workstream_id?.trim().toLowerCase()
        )) ||
      (selectedWorkstream?.workstream_name != null &&
        workstreamSource.workstreamName?.trim().toLowerCase() ===
          selectedWorkstream.workstream_name.trim().toLowerCase())
  );
  const isDay1ReadinessLoading =
    isOpen &&
    day1ReadinessQuery.isLoading &&
    day1ReadinessInsight == null;
  const day1ReadinessUnderlyingError = day1ReadinessQuery.error;
  const isPersonaNotAssignedError =
    isApiError(day1ReadinessUnderlyingError) && day1ReadinessUnderlyingError.code === PERSONA_NOT_ASSIGNED_ERROR_CODE;
  const isNoDataError = isApiError(day1ReadinessUnderlyingError) && day1ReadinessUnderlyingError.code === "no_data";
  const isReadinessWaiting = day1ReadinessQuery.mappingReason != null;
  const day1ReadinessError =
    isOpen && !isDay1ReadinessLoading && day1ReadinessInsight == null
      ? (isPersonaNotAssignedError
          ? "No AI Insights persona is assigned to your account yet."
          : day1ReadinessQuery.isError && !isNoDataError && !isReadinessWaiting
            ? "Unable to load insights right now. Please try again shortly."
            : null)
      : null;
  const day1ReadinessUnavailableMessage =
    isOpen && !isDay1ReadinessLoading && day1ReadinessInsight == null
      ? getAiInsightsAvailabilityMessage(
          isApiError(day1ReadinessUnderlyingError) ? day1ReadinessUnderlyingError.code : undefined,
          "Day 1 readiness"
        )
      : null;

  const hasShownPersonaToastRef = useRef(false);
  useEffect(() => {
    if (!isOpen) {
      hasShownPersonaToastRef.current = false;
      return;
    }
    if (isPersonaNotAssignedError && !hasShownPersonaToastRef.current) {
      hasShownPersonaToastRef.current = true;
      toast.error("No AI Insights persona is assigned to your account yet. Contact an admin to get one assigned.");
    }
  }, [isOpen, isPersonaNotAssignedError]);

  if (isDay1ReadinessLoading) {
    return (
      <section className="global-insights-overlay__section global-insights-overlay__section--loading" aria-live="polite">
        <Spinner size="lg" />
      </section>
    );
  }

  if (day1ReadinessError != null) {
    return (
      <section className="global-insights-overlay__section" aria-live="assertive">
        <p className="global-insights-overlay__raid-status-text global-insights-overlay__raid-status-text--error">
          {day1ReadinessError}
        </p>
      </section>
    );
  }

  if (day1ReadinessUnavailableMessage != null) {
    return isReadinessWaiting ? (
      <AiInsightWaitingState reason={day1ReadinessQuery.mappingReason} />
    ) : (
      <section className="global-insights-overlay__section" aria-live="polite">
        <p className="global-insights-overlay__raid-status-text">{day1ReadinessUnavailableMessage}</p>
      </section>
    );
  }

  if (day1ReadinessInsight == null) {
    return null;
  }

  const workstreams = Array.isArray(day1ReadinessInsight.workstreams) ? day1ReadinessInsight.workstreams : [];
  const contextWorkstreams = day1ContextQuery.data?.pageData?.workstreams ?? [];
  const prioritisedWorkstreamIds = Array.isArray(day1ReadinessInsight.prioritised_workstream_ids)
    ? day1ReadinessInsight.prioritised_workstream_ids
    : null;
  const workstreamsById = new Map<string, Day1ReadinessWorkstreamDto>(
    workstreams.flatMap((workstream) => {
      const id = workstream.workstream_id?.trim().toLowerCase();
      return id ? [[id, workstream]] : [];
    }),
  );
  const prioritisedWorkstreams = prioritisedWorkstreamIds?.length
    ? prioritisedWorkstreamIds
        .map((workstreamId) => workstreamsById.get(workstreamId.trim().toLowerCase()))
        .filter((workstream): workstream is Day1ReadinessWorkstreamDto => workstream != null)
    : [];
  // An empty or stale prioritisation list should not hide the objective assessments.
  const displayedWorkstreams = (prioritisedWorkstreams.length > 0 ? prioritisedWorkstreams : workstreams).slice(0, 5);
  const keyRecommendations = Array.isArray(day1ReadinessInsight.key_recommendations_for_me)
    ? day1ReadinessInsight.key_recommendations_for_me
    : [];
  const formattedConfidenceBand = formatDisplayValue(day1ReadinessInsight.overall_readiness?.confidence_band);
  const summarySubtitle = [
    formattedConfidenceBand != null ? `Confidence: ${formattedConfidenceBand}` : null,
    formatCounts(day1ReadinessInsight.overall_readiness?.workstream_counts),
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <>
      <section
        className="global-insights-overlay__section global-insights-overlay__section--raid-health"
        aria-labelledby="global-insights-day1-health-title"
      >
        <div className="global-insights-overlay__section-header">
          <h3
            id="global-insights-day1-health-title"
            className="global-insights-overlay__section-title global-insights-overlay__section-title--raid"
          >
            Day 1 Readiness
          </h3>
          <AiInsightSourceMarker />
        </div>
        <div className="global-insights-overlay__raid-health-card">
          <p className="global-insights-overlay__raid-health-text">
            {day1ReadinessInsight.overall_readiness?.summary ?? "Not available"}
          </p>

          {summarySubtitle !== "" && <p className="global-insights-overlay__raid-subtitle">{summarySubtitle}</p>}
        </div>
        {day1ReadinessResponse?.status === "ready" && (
          <AiOutputFeedbackControls
            executionId={day1ReadinessResponse.aiOutputExecutionId}
            outputType="ai_insight"
            className="global-insights-overlay__feedback"
          />
        )}
      </section>

      <section className="global-insights-overlay__section" aria-labelledby="global-insights-day1-workstreams-title">
        <h3
          id="global-insights-day1-workstreams-title"
          className="global-insights-overlay__section-title global-insights-overlay__section-title--raid"
        >
          Workstreams requiring attention
        </h3>

        {displayedWorkstreams.length > 0 ? (
          <div className="global-insights-overlay__raid-driver-list">
            {displayedWorkstreams.map((workstream, index) => (
                <article
                  key={workstream.workstream_id ?? workstream.workstream_name ?? `workstream-${index}`}
                  className="global-insights-overlay__raid-driver-card global-insights-overlay__raid-driver-card--clickable"
                  role="button"
                  tabIndex={0}
                  onClick={() => setSelectedWorkstream(workstream)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      setSelectedWorkstream(workstream);
                    }
                  }}
                >
                  <div className="global-insights-overlay__raid-driver-copy">
                    <h4 className="global-insights-overlay__raid-driver-title global-insights-overlay__raid-driver-title--with-chevron">
                      <span>{workstream.workstream_name ?? "Not available"}</span>
                      <FaChevronRight aria-hidden="true" />
                    </h4>
                  </div>
                </article>
              ))}
          </div>
        ) : (
          <p className="global-insights-overlay__raid-status-text">No workstreams requiring attention available.</p>
        )}
      </section>

      <section className="global-insights-overlay__section" aria-labelledby="global-insights-day1-recommendations-title">
        <h3
          id="global-insights-day1-recommendations-title"
          className="global-insights-overlay__section-title global-insights-overlay__section-title--raid"
        >
          Key recommendations for me
        </h3>

        {keyRecommendations.length > 0 ? (
          <div className="global-insights-overlay__planning-recommendation-list">
            {keyRecommendations.map((item, index) => (
              <Day1RecommendationCard
                key={`${item.title}-${index}`}
                item={item}
                workstreams={workstreams}
                contextWorkstreams={contextWorkstreams}
                onSelectWorkstream={setSelectedWorkstream}
              />
            ))}
          </div>
        ) : (
          <p className="global-insights-overlay__raid-status-text">No key recommendations available.</p>
        )}
      </section>

      {isDataQualityVisible && (
        <AiDataQualitySection
          headingId="global-insights-day1-quality-title"
          dataQuality={day1ReadinessInsight.data_quality_summary}
          emptyStateDescription="The available Day 1 readiness data supports the current analysis."
        />
      )}

      <AiInsightDeveloperContext
        developerContext={{
          assessmentStatus: day1ReadinessInsight.assessment_status,
          dataQualityAndCaveats: day1ReadinessInsight.data_missing,
        }}
      />

      {isOpen && selectedWorkstream != null && (
        <AiInsightsDay1ReadinessWorkstreamDetailModal
          workstream={selectedWorkstream}
          lastUpdatedAt={
            selectedWorkstreamSource?.last_updated_at ?? selectedWorkstreamSource?.lastUpdatedAt
          }
          onClose={() => setSelectedWorkstream(null)}
        />
      )}
    </>
  );
}

export default AiInsightsDay1ReadinessShelf;
